import { openDB } from 'idb';

class LocalDBService {
  constructor() {
    this.dbName = 'intervention-drafts';
    this.version = 2; // Augmenter la version pour ajouter les tables de tâches
    this.init();
  }

  async init() {
    this.db = await openDB(this.dbName, this.version, {
      upgrade(db, oldVersion, newVersion) {
        // Table pour les brouillons d'interventions
        if (!db.objectStoreNames.contains('intervention_drafts')) {
          const draftStore = db.createObjectStore('intervention_drafts', { keyPath: 'intervention_id' });
          draftStore.createIndex('last_modified', 'last_modified');
          draftStore.createIndex('is_synced', 'is_synced');
        }
        
        // Table pour les photos temporaires
        if (!db.objectStoreNames.contains('temp_photos')) {
          const photoStore = db.createObjectStore('temp_photos', { keyPath: 'id', autoIncrement: true });
          photoStore.createIndex('draft_id', 'draft_id');
          photoStore.createIndex('is_uploaded', 'is_uploaded');
        }

        // Ajouter les tables pour les tâches (version 2)
        if (newVersion >= 2) {
          // Table pour les brouillons de tâches
          if (!db.objectStoreNames.contains('task_drafts')) {
            const taskDraftStore = db.createObjectStore('task_drafts', { keyPath: 'task_id' });
            taskDraftStore.createIndex('last_modified', 'last_modified');
            taskDraftStore.createIndex('is_synced', 'is_synced');
          }
          
          // Table pour les photos temporaires de tâches
          if (!db.objectStoreNames.contains('temp_task_photos')) {
            const taskPhotoStore = db.createObjectStore('temp_task_photos', { keyPath: 'id', autoIncrement: true });
            taskPhotoStore.createIndex('draft_id', 'draft_id');
            taskPhotoStore.createIndex('is_uploaded', 'is_uploaded');
          }
        }
      }
    });
  }

  // === MÉTHODES POUR LES INTERVENTIONS ===

  // Sauvegarder un brouillon d'intervention
  async saveDraft(interventionId, data) {
    const draft = {
      intervention_id: interventionId,
      ...data,
      last_modified: new Date().toISOString(),
      is_synced: false
    };

    await this.db.put('intervention_drafts', draft);
    console.log(`Brouillon sauvegardé pour l'intervention ${interventionId} avec UUID: ${data.version_uuid || 'non défini'}`);
  }

  // Récupérer un brouillon d'intervention
  async getDraft(interventionId) {
    try {
      return await this.db.get('intervention_drafts', interventionId);
    } catch (error) {
      console.error('Erreur récupération brouillon:', error);
      return null;
    }
  }

  // Supprimer un brouillon d'intervention (après sync réussie)
  async deleteDraft(interventionId) {
    try {
      const exists = await this.db.get('intervention_drafts', interventionId);
      if (exists) {
        await this.db.delete('intervention_drafts', interventionId);
      }
    } catch (error) {
      console.error('Erreur suppression brouillon:', error);
      throw error; // Propager l'erreur pour la gestion en amont
    }
  }

  // Vérifier si le brouillon local d'intervention est à jour
  async isLocalUpToDate(interventionId, serverLastModified) {
    const localDraft = await this.getDraft(interventionId);
    if (!localDraft) return true; // Pas de brouillon local = à jour

    const localDate = new Date(localDraft.last_modified);
    const serverDate = new Date(serverLastModified);
    
    return localDate <= serverDate;
  }

  // Marquer un brouillon d'intervention comme synchronisé
  async markAsSynced(interventionId) {
    const draft = await this.getDraft(interventionId);
    if (draft) {
      draft.is_synced = true;
      draft.last_modified = new Date().toISOString();
      await this.db.put('intervention_drafts', draft);
    }
  }

  // Sauvegarder des photos temporaires d'intervention
  async saveTempPhotos(interventionId, photos, type) {
    // Récupérer les photos existantes pour éviter les doublons
    const existingPhotos = await this.getTempPhotos(interventionId, type);
    const existingPhotoIds = existingPhotos.map(p => p.temp_id); // Utiliser l'ID temporaire comme identifiant unique
    
    // Filtrer pour ne garder que les nouvelles photos
    const newPhotos = photos.filter(photo => !existingPhotoIds.includes(photo.id));
    
    console.log(`Sauvegarde photos ${type}:`, {
      total: photos.length,
      existantes: existingPhotos.length,
      nouvelles: newPhotos.length,
      existingIds: existingPhotoIds,
      newIds: newPhotos.map(p => p.id)
    });
    
    if (newPhotos.length === 0) {
      console.log('Aucune nouvelle photo à sauvegarder');
      return;
    }
    
    const photoPromises = newPhotos.map(photo => {
      return this.db.add('temp_photos', {
        draft_id: interventionId,
        photo_type: type,
        photo_name: photo.name,
        photo_data: photo.data,
        temp_id: photo.id, // Stocker l'ID temporaire pour éviter les doublons
        is_uploaded: false
      });
    });

    await Promise.all(photoPromises);
    console.log(`${newPhotos.length} nouvelles photos ${type} sauvegardées`);
  }

  // Récupérer les photos temporaires d'intervention
  async getTempPhotos(interventionId, type) {
    const photos = await this.db.getAllFromIndex('temp_photos', 'draft_id', interventionId);
    return photos.filter(photo => photo.photo_type === type);
  }

  // Supprimer les photos temporaires d'intervention après upload
  async deleteTempPhotos(interventionId) {
    try {
      const photos = await this.db.getAllFromIndex('temp_photos', 'draft_id', interventionId);
      
      if (photos.length > 0) {
        const deletePromises = photos.map(photo => this.db.delete('temp_photos', photo.id));
        await Promise.all(deletePromises);
      }
    } catch (error) {
      console.error('Erreur suppression photos temporaires:', error);
      throw error; // Propager l'erreur pour la gestion en amont
    }
  }

  // Supprimer une photo temporaire d'intervention spécifique
  async deleteTempPhoto(photoId) {
    try {
      await this.db.delete('temp_photos', photoId);
      console.log(`Photo temporaire ${photoId} supprimée de la DB locale`);
    } catch (error) {
      console.error('Erreur suppression photo temporaire:', error);
    }
  }

  // Nettoyer complètement les données d'une intervention spécifique
  async cleanInterventionData(interventionId) {
    try {
      console.log(`🧹 Début du nettoyage pour l'intervention ${interventionId}`);
      
      // Vérifier l'état initial
      const initialDraft = await this.getDraft(interventionId);
      const initialClientPhotos = await this.getTempPhotos(interventionId, 'client');
      const initialUserPhotos = await this.getTempPhotos(interventionId, 'utilisateur');
      
      console.log('État initial:', {
        draft: !!initialDraft,
        clientPhotos: initialClientPhotos.length,
        userPhotos: initialUserPhotos.length
      });
      
      // Supprimer le brouillon
      console.log('Suppression du brouillon...');
      await this.deleteDraft(interventionId);
      
      // Vérifier que le brouillon est supprimé
      const draftAfterDelete = await this.getDraft(interventionId);
      if (draftAfterDelete) {
        console.log('❌ Le brouillon existe encore après suppression, tentative forcée...');
        // Essayer une suppression forcée
        try {
          await this.db.delete('intervention_drafts', interventionId);
          console.log('✅ Suppression forcée réussie');
        } catch (forceError) {
          console.error('❌ Erreur suppression forcée:', forceError);
        }
      } else {
        console.log('✅ Brouillon supprimé avec succès');
      }
      
      // Supprimer les photos temporaires
      console.log('Suppression des photos temporaires...');
      await this.deleteTempPhotos(interventionId);
      
      // Vérification finale
      const finalDraft = await this.getDraft(interventionId);
      const finalClientPhotos = await this.getTempPhotos(interventionId, 'client');
      const finalUserPhotos = await this.getTempPhotos(interventionId, 'utilisateur');
      
      console.log('État final:', {
        draft: !!finalDraft,
        clientPhotos: finalClientPhotos.length,
        userPhotos: finalUserPhotos.length
      });
      
      if (!finalDraft && finalClientPhotos.length === 0 && finalUserPhotos.length === 0) {
        console.log(`✅ Nettoyage réussi pour l'intervention ${interventionId}`);
      } else {
        console.warn(`⚠️ Données restantes pour l'intervention ${interventionId}:`, {
          draft: !!finalDraft,
          clientPhotos: finalClientPhotos.length,
          userPhotos: finalUserPhotos.length
        });
        
        // Si le brouillon reste, essayer une dernière fois
        if (finalDraft) {
          console.log('Tentative de suppression finale du brouillon...');
          try {
            await this.db.delete('intervention_drafts', interventionId);
            console.log('✅ Suppression finale réussie');
          } catch (finalError) {
            console.error('❌ Échec de la suppression finale:', finalError);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du nettoyage des données:', error);
      throw error;
    }
  }

  // Vérifier l'état d'une intervention spécifique
  async checkInterventionStatus(interventionId) {
    try {
      const draft = await this.getDraft(interventionId);
      const clientPhotos = await this.getTempPhotos(interventionId, 'client');
      const userPhotos = await this.getTempPhotos(interventionId, 'utilisateur');
      
      console.log(`📊 État de l'intervention ${interventionId}:`, {
        draft: draft ? {
          exists: true,
          lastModified: draft.last_modified,
          isSynced: draft.is_synced
        } : { exists: false },
        clientPhotos: clientPhotos.length,
        userPhotos: userPhotos.length,
        totalPhotos: clientPhotos.length + userPhotos.length
      });
      
      return { draft, clientPhotos, userPhotos };
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);
      return null;
    }
  }

  // === MÉTHODES POUR LES TÂCHES ===

  // Sauvegarder un brouillon de tâche
  async saveTaskDraft(taskId, data) {
    const draft = {
      task_id: taskId,
      ...data,
      last_modified: new Date().toISOString(),
      is_synced: false
    };

    await this.db.put('task_drafts', draft);
    console.log(`Brouillon sauvegardé pour la tâche ${taskId}`);
  }

  // Récupérer un brouillon de tâche
  async getTaskDraft(taskId) {
    try {
      return await this.db.get('task_drafts', taskId);
    } catch (error) {
      console.error('Erreur récupération brouillon de tâche:', error);
      return null;
    }
  }

  // Supprimer un brouillon de tâche (après sync réussie)
  async deleteTaskDraft(taskId) {
    try {
      const exists = await this.db.get('task_drafts', taskId);
      if (exists) {
        await this.db.delete('task_drafts', taskId);
      }
    } catch (error) {
      console.error('Erreur suppression brouillon de tâche:', error);
      throw error;
    }
  }

  // Vérifier si le brouillon local de tâche est à jour
  async isTaskLocalUpToDate(taskId, serverLastModified) {
    const localDraft = await this.getTaskDraft(taskId);
    if (!localDraft) return true; // Pas de brouillon local = à jour

    const localDate = new Date(localDraft.last_modified);
    const serverDate = new Date(serverLastModified);
    
    return localDate <= serverDate;
  }

  // Marquer un brouillon de tâche comme synchronisé
  async markTaskAsSynced(taskId) {
    const draft = await this.getTaskDraft(taskId);
    if (draft) {
      draft.is_synced = true;
      draft.last_modified = new Date().toISOString();
      await this.db.put('task_drafts', draft);
    }
  }

  // Sauvegarder des photos temporaires de tâche
  async saveTempTaskPhotos(taskId, photos) {
    // Récupérer les photos existantes pour éviter les doublons
    const existingPhotos = await this.getTempTaskPhotos(taskId);
    const existingPhotoIds = existingPhotos.map(p => p.temp_id);
    
    // Filtrer pour ne garder que les nouvelles photos
    const newPhotos = photos.filter(photo => !existingPhotoIds.includes(photo.id));
    
    console.log(`Sauvegarde photos de tâche:`, {
      total: photos.length,
      existantes: existingPhotos.length,
      nouvelles: newPhotos.length,
      existingIds: existingPhotoIds,
      newIds: newPhotos.map(p => p.id)
    });
    
    if (newPhotos.length === 0) {
      console.log('Aucune nouvelle photo de tâche à sauvegarder');
      return;
    }
    
    const photoPromises = newPhotos.map(photo => {
      return this.db.add('temp_task_photos', {
        draft_id: taskId,
        photo_name: photo.name,
        photo_data: photo.data,
        temp_id: photo.id,
        is_uploaded: false
      });
    });

    await Promise.all(photoPromises);
    console.log(`${newPhotos.length} nouvelles photos de tâche sauvegardées`);
  }

  // Récupérer les photos temporaires de tâche
  async getTempTaskPhotos(taskId) {
    return await this.db.getAllFromIndex('temp_task_photos', 'draft_id', taskId);
  }

  // Supprimer les photos temporaires de tâche après upload
  async deleteTempTaskPhotos(taskId) {
    try {
      const photos = await this.db.getAllFromIndex('temp_task_photos', 'draft_id', taskId);
      
      if (photos.length > 0) {
        const deletePromises = photos.map(photo => this.db.delete('temp_task_photos', photo.id));
        await Promise.all(deletePromises);
      }
    } catch (error) {
      console.error('Erreur suppression photos temporaires de tâche:', error);
      throw error;
    }
  }

  // Nettoyer complètement les données d'une tâche spécifique
  async cleanTaskData(taskId) {
    try {
      console.log(`🧹 Début du nettoyage pour la tâche ${taskId}`);
      
      // Vérifier l'état initial
      const initialDraft = await this.getTaskDraft(taskId);
      const initialPhotos = await this.getTempTaskPhotos(taskId);
      
      console.log('État initial:', {
        draft: !!initialDraft,
        photos: initialPhotos.length
      });
      
      // Supprimer le brouillon
      console.log('Suppression du brouillon...');
      await this.deleteTaskDraft(taskId);
      
      // Supprimer les photos temporaires
      console.log('Suppression des photos temporaires...');
      await this.deleteTempTaskPhotos(taskId);
      
      // Vérification finale
      const finalDraft = await this.getTaskDraft(taskId);
      const finalPhotos = await this.getTempTaskPhotos(taskId);
      
      console.log('État final:', {
        draft: !!finalDraft,
        photos: finalPhotos.length
      });
      
      if (!finalDraft && finalPhotos.length === 0) {
        console.log(`✅ Nettoyage réussi pour la tâche ${taskId}`);
      } else {
        console.warn(`⚠️ Données restantes pour la tâche ${taskId}:`, {
          draft: !!finalDraft,
          photos: finalPhotos.length
        });
      }
    } catch (error) {
      console.error('Erreur lors du nettoyage des données de tâche:', error);
      throw error;
    }
  }

  // Vérifier l'état d'une tâche spécifique
  async checkTaskStatus(taskId) {
    try {
      const draft = await this.getTaskDraft(taskId);
      const photos = await this.getTempTaskPhotos(taskId);
      
      console.log(`📊 État de la tâche ${taskId}:`, {
        draft: draft ? {
          exists: true,
          lastModified: draft.last_modified,
          isSynced: draft.is_synced
        } : { exists: false },
        photos: photos.length
      });
      
      return { draft, photos };
    } catch (error) {
      console.error('Erreur lors de la vérification de tâche:', error);
      return null;
    }
  }

  // Vider complètement la DB locale (pour les tests)
  async clearAll() {
    try {
      await this.db.clear('intervention_drafts');
      await this.db.clear('temp_photos');
      await this.db.clear('task_drafts');
      await this.db.clear('temp_task_photos');
      console.log('DB locale complètement vidée');
    } catch (error) {
      console.error('Erreur lors du vidage de la DB locale:', error);
    }
  }
}

export default new LocalDBService(); 